import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  selectDoraFinancialEntities,
  setDoraFinancialEntities,
} from '../store/financialEntity/financialEntitySlice.ts';
import {
  addDoraBusinessService,
  removeDoraBusinessService,
  selectDoraBusinessServices,
  setDoraBusinessServices,
  updateDoraBusinessService as updateDoraBusinessServiceState,
} from '../store/businessService/businessServiceSlice.ts';
import {
  createDoraBusinessService,
  deleteDoraBusinessService,
  listDoraBusinessServices,
  listDoraFinancialEntities,
  updateDoraBusinessService,
} from '../../../api.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import { useExcelTableState } from '../../../hooks/useExcelTableState.ts';
import {
  parsePasteData,
  calculateNextCell,
  applyPastedData,
  getRowBackgroundColor,
  getRowHoverColor,
} from '../../../utils/excelTableUtils.ts';

interface EditableRow {
  id?: number;
  name: string;
  description: string;
  is_critical: boolean;
  financial_entity_id: number | '';
  isNew?: boolean;
  touched?: boolean;
}

export const BusinessServicesPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const financialEntities = useAppSelector(selectDoraFinancialEntities);
  const businessServices = useAppSelector(selectDoraBusinessServices);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editableRows, setEditableRows] = useState<EditableRow[]>([]);

  const {
    focusedRowIndex,
    editingCell,
    setEditingCell,
    handleRowFocus: baseHandleRowFocus,
    handleRowBlur: baseHandleRowBlur,
    isSaving,
    markSaving,
    markSaved,
  } = useExcelTableState<EditableRow>();

  // Convert business services to editable rows and always add an empty row at the end
  const rows = useMemo(() => {
    const existingRows: EditableRow[] = businessServices.map(s => ({
      id: s.id,
      name: s.name,
      description: s.description || '',
      is_critical: s.is_critical,
      financial_entity_id: s.financial_entity_id,
    }));

    // Add empty row for new entries
    const emptyRow: EditableRow = {
      name: '',
      description: '',
      is_critical: true,
      financial_entity_id: financialEntities[0]?.id ?? '',
      isNew: true,
    };

    return [...existingRows, emptyRow];
  }, [businessServices, financialEntities]);

  useEffect(() => {
    setEditableRows(rows);
  }, [rows]);

  const fieldOrder: (keyof EditableRow)[] = useMemo(() =>
    ['name', 'description', 'financial_entity_id', 'is_critical'],
    []
  );

  useEffect(() => {
    const load = async () => {
      if (!currentOrganization) return;
      setLoading(true);
      setError(null);
      try {
        const [fes, services] = await Promise.all([
          listDoraFinancialEntities(),
          listDoraBusinessServices(),
        ]);
        dispatch(setDoraFinancialEntities(fes));
        dispatch(setDoraBusinessServices(services));
      } catch (err: any) {
        console.error('Failed to load business services', err);
        setError(err?.response?.data?.detail || 'Failed to load business services');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrganization, dispatch]);

  const updateRow = (index: number, field: keyof EditableRow, value: any) => {
    setEditableRows(prev => {
      const updated = [...prev];
      const row = updated[index];
      // Mark new rows as touched when first edited
      updated[index] = {
        ...row,
        [field]: value,
        ...(row.isNew && !row.touched ? { touched: true } : {})
      };
      return updated;
    });
  };

  // Helper to determine if a required field is empty
  const isRequiredFieldEmpty = (row: EditableRow, field: keyof EditableRow): boolean => {
    const requiredFields: (keyof EditableRow)[] = ['name', 'financial_entity_id'];
    if (!requiredFields.includes(field)) return false;

    const value = row[field];
    return value === '' || value === null || value === undefined;
  };

  // Helper to get cell background color with subtle amber shine for required empty fields
  const getCellBackgroundColor = (row: EditableRow, field: keyof EditableRow): string => {
    // Don't highlight untouched new rows
    if (row.isNew && !row.touched) {
      return 'transparent';
    }

    if (isRequiredFieldEmpty(row, field)) {
      // Brighter, more fancy amber/gold shine with subtle gradient effect
      return 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 160, 0, 0.08))';
    }
    return 'transparent';
  };

  const handleRowFocus = (index: number) => {
    baseHandleRowFocus(index, editableRows, saveRow);
  };

  const handleRowBlur = () => {
    baseHandleRowBlur(editableRows, saveRow);
  };

  const handleTabNavigation = (e: React.KeyboardEvent, currentRow: number, currentField: keyof EditableRow) => {
    if (e.key !== 'Tab') return;

    e.preventDefault();

    const currentFieldIndex = fieldOrder.indexOf(currentField);
    const nextCell = calculateNextCell(
      currentRow,
      currentFieldIndex,
      editableRows.length,
      fieldOrder.length,
      e.shiftKey
    );

    if (nextCell) {
      if (nextCell.row !== currentRow) {
        handleRowFocus(nextCell.row);
      }
      setEditingCell({ row: nextCell.row, field: fieldOrder[nextCell.fieldIndex] });
    }
  };

  const saveRow = useCallback(async (row: EditableRow, index: number) => {
    const rowKey = row.id ?? `new-${index}`;

    // Prevent duplicate saves
    if (isSaving(rowKey)) return;

    // Validate required fields
    const requiredFieldsFilled = row.name.trim() && row.financial_entity_id;

    try {
      if (row.isNew) {
        // Create new business service only if required fields are filled
        if (requiredFieldsFilled) {
          markSaving(rowKey);
          const payload = {
            name: row.name.trim(),
            description: row.description || null,
            is_critical: row.is_critical,
            financial_entity_id: Number(row.financial_entity_id),
          };
          const created = await createDoraBusinessService(payload);
          dispatch(addDoraBusinessService(created));
        }
      } else if (row.id) {
        // Update existing business service
        if (requiredFieldsFilled) {
          markSaving(rowKey);
          const payload = {
            name: row.name.trim(),
            description: row.description || null,
            is_critical: row.is_critical,
            financial_entity_id: Number(row.financial_entity_id),
          };
          const updated = await updateDoraBusinessService(row.id, payload);
          dispatch(updateDoraBusinessServiceState(updated));
        }
      }
    } catch (err: any) {
      console.error('Failed to save business service', err);
      setError(err?.response?.data?.detail || 'Failed to save business service');
    } finally {
      markSaved(rowKey);
    }
  }, [dispatch, isSaving, markSaving, markSaved]);

  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm('Delete this business service?')) return;
    setLoading(true);
    setError(null);
    try {
      await deleteDoraBusinessService(id);
      dispatch(removeDoraBusinessService(id));
    } catch (err: any) {
      console.error('Failed to delete business service', err);
      setError(err?.response?.data?.detail || 'Failed to delete business service');
    } finally {
      setLoading(false);
    }
  };

  const handlePaste = useCallback((e: React.ClipboardEvent, startRowIndex: number, startField: keyof EditableRow) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const startFieldIndex = fieldOrder.indexOf(startField);

    // Parse the pasted data
    const parsedRows = parsePasteData(pasteData);

    // Normalize parsed data: treat visual "empty" representations as actual empty strings
    const normalizedRows = parsedRows.map(row =>
      row.map(cell => {
        const trimmed = cell.trim();
        // Treat various dash characters and common empty representations as empty
        if (trimmed === '—' || trimmed === '-' || trimmed === '–' || trimmed === '−' || trimmed === 'N/A' || trimmed === 'n/a') {
          return '';
        }
        return cell;
      })
    );

    // Create a map of related entities for foreign key matching
    const relatedEntitiesMap = new Map<keyof EditableRow, typeof financialEntities>();
    relatedEntitiesMap.set('financial_entity_id', financialEntities);

    // Apply pasted data to rows
    const createEmptyRow = (): EditableRow => ({
      name: '',
      description: '',
      is_critical: true,
      financial_entity_id: financialEntities[0]?.id ?? '',
      isNew: true,
      touched: true, // Pasted rows are considered touched
    });

    setEditableRows(prev => {
      const updated = applyPastedData(
        prev,
        normalizedRows,
        startRowIndex,
        startFieldIndex,
        fieldOrder,
        relatedEntitiesMap,
        createEmptyRow
      );
      // Mark affected rows as touched
      return updated.map((row, idx) => {
        if (idx >= startRowIndex && idx < startRowIndex + normalizedRows.length && row.isNew) {
          return { ...row, touched: true };
        }
        return row;
      });
    });

    // Auto-save pasted rows after a short delay
    setTimeout(() => {
      normalizedRows.forEach((_, rowOffset) => {
        const targetRowIndex = startRowIndex + rowOffset;
        if (targetRowIndex < editableRows.length) {
          saveRow(editableRows[targetRowIndex], targetRowIndex);
        }
      });
    }, 300);
  }, [editableRows, financialEntities, saveRow, fieldOrder]);

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      {financialEntities.length === 0 && (
        <Alert severity="warning">
          No Financial Entities found. Please create financial entities in the <strong>Financial Entities</strong> panel (B_01.02) before creating business services.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Business Services</Typography>
            {loading && <CircularProgress size={20} />}
          </Stack>

          <TableContainer sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    Description
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    Financial Entity
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                    Critical
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 60 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editableRows.map((row, index) => {
                  const isFocused = focusedRowIndex === index;
                  const isEvenRow = index % 2 === 0;
                  const feName = financialEntities.find(fe => fe.id === row.financial_entity_id)?.name || '';

                  return (
                    <TableRow
                      key={row.id ?? `new-${index}`}
                      sx={{
                        bgcolor: getRowBackgroundColor(isFocused, row.isNew || false, isEvenRow),
                        '&:hover': {
                          bgcolor: getRowHoverColor(isFocused),
                        },
                        transition: 'background-color 0.15s ease',
                      }}
                    >
                      {/* Name Field */}
                      <TableCell width="25%">
                        {editingCell?.row === index && editingCell?.field === 'name' ? (
                          <TextField
                            value={row.name}
                            onChange={(e) => updateRow(index, 'name', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'name')}
                            onPaste={(e) => handlePaste(e, index, 'name')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder={row.isNew ? 'Enter name...' : ''}
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'name' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'name'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.name || (row.isNew ? <span style={{ color: '#999' }}>Enter name...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Description Field */}
                      <TableCell width="30%">
                        {editingCell?.row === index && editingCell?.field === 'description' ? (
                          <TextField
                            value={row.description}
                            onChange={(e) => updateRow(index, 'description', e.target.value)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'description')}
                            onPaste={(e) => handlePaste(e, index, 'description')}
                            size="small"
                            fullWidth
                            variant="standard"
                            placeholder={row.isNew ? 'Enter description...' : ''}
                            InputProps={{ disableUnderline: true }}
                            sx={{ '& input': { p: 1 } }}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'description' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'text',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'description'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {row.description || (row.isNew ? <span style={{ color: '#999' }}>Enter description...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Financial Entity Field */}
                      <TableCell width="25%">
                        {editingCell?.row === index && editingCell?.field === 'financial_entity_id' ? (
                          <FormControl fullWidth size="small" variant="standard">
                            <Select
                              value={row.financial_entity_id === '' ? '' : String(row.financial_entity_id)}
                              onChange={(e: SelectChangeEvent<string>) => {
                                updateRow(index, 'financial_entity_id', Number(e.target.value));
                              }}
                              onBlur={() => {
                                handleRowBlur();
                                setEditingCell(null);
                              }}
                              onKeyDown={(e) => handleTabNavigation(e, index, 'financial_entity_id')}
                              displayEmpty
                              disableUnderline
                              sx={{ '& .MuiSelect-select': { p: 1 } }}
                              autoFocus
                            >
                              {row.isNew && <MenuItem value=""><em>Select...</em></MenuItem>}
                              {financialEntities.map(fe => (
                                <MenuItem key={fe.id} value={fe.id}>{fe.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'financial_entity_id' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                              background: getCellBackgroundColor(row, 'financial_entity_id'),
                              transition: 'background 0.15s ease',
                            }}
                          >
                            {feName || (row.isNew ? <span style={{ color: '#999' }}>Select...</span> : '')}
                          </div>
                        )}
                      </TableCell>

                      {/* Critical Field */}
                      <TableCell width="10%">
                        {editingCell?.row === index && editingCell?.field === 'is_critical' ? (
                          <Checkbox
                            checked={row.is_critical}
                            onChange={(e) => updateRow(index, 'is_critical', e.target.checked)}
                            onBlur={() => {
                              handleRowBlur();
                              setEditingCell(null);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, index, 'is_critical')}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              handleRowFocus(index);
                              setEditingCell({ row: index, field: 'is_critical' });
                            }}
                            style={{
                              padding: '8px',
                              minHeight: '20px',
                              cursor: 'pointer',
                              userSelect: 'text',
                            }}
                          >
                            {row.is_critical ? 'Yes' : 'No'}
                          </div>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        {!row.isNew && (
                          <IconButton
                            onClick={() => handleDelete(row.id)}
                            size="small"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default BusinessServicesPanel;
